import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, PartyPopper, PlusCircle, Repeat, Clock, AlertTriangle, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAITransition } from "@/components/AITransition";
import { getCart, setCart, cartTotal, clearCart } from "@/lib/cart";
import { payForBooking } from "@/lib/razorpay";
import { waLink, bookingChatText } from "@/lib/whatsapp";
import ProductPickerModal from "@/components/ProductPickerModal";

// Delivery module — 4 named slots with display ranges. The slot KEY is sent
// to the backend; fee pricing is derived from date + slot (see below).
const TIME_SLOTS = [
  { key: "morning",   label: "Morning",   range: "8:00 AM – 12:00 PM" },
  { key: "afternoon", label: "Afternoon", range: "12:00 PM – 4:00 PM" },
  { key: "evening",   label: "Evening",   range: "4:00 PM – 8:00 PM" },
  { key: "night",     label: "Night",     range: "8:00 PM – 11:00 PM" },
];
const EXPRESS_SLOTS = new Set(["evening", "night"]);
// Fallbacks only — the real values come from GET /api/settings/public
// (admin-configurable). Used if the fetch fails so checkout never breaks.
const DEFAULT_DELIVERY_PRICING = {
  standard_fee: 199,
  express_fee: 499,
  plan_ahead_discount: 99,
  plan_ahead_min_days: 3,
};

const lineKeyOf = (i) =>
  i.line_key || (i.kind === "experience" ? `exp:${i.experience_id}` : i.product_id);

const isExperience = (i) => i.kind === "experience";
const isSourceable = (i) => i.kind === "sourceable";

const priceLineTotal = (i) => {
  if (isSourceable(i)) return 0;
  if (isExperience(i) && i.price_type === "on_request") return 0;
  return (i.price || 0) * (i.quantity || 1);
};

const priceLabel = (i) => {
  if (isSourceable(i)) return "Price to be confirmed";
  if (isExperience(i) && i.price_type === "on_request") return "Price on request";
  if (isExperience(i) && i.price_type === "approx") return `~ ₹${priceLineTotal(i)}`;
  return `₹${priceLineTotal(i)}`;
};

export default function Checkout() {
  const [items, setItems] = useState(getCart());
  const [date, setDate] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [slot, setSlot] = useState("morning");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [pincode, setPincode] = useState("");
  const [notes, setNotes] = useState("");
  const [relationshipId, setRelationshipId] = useState("");
  const [occasion, setOccasion] = useState("");
  const [relationships, setRelationships] = useState([]);
  const [deliveryPricing, setDeliveryPricing] = useState(DEFAULT_DELIVERY_PRICING);
  const [expDetails, setExpDetails] = useState({}); // { [line_key]: {date, persons, whatsapp, special_request} }
  const [busy, setBusy] = useState(false);
  const [businessWhatsapp, setBusinessWhatsapp] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState("add"); // "add" | "change"
  const [changingLineKey, setChangingLineKey] = useState(null);
  const [changingCategory, setChangingCategory] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const playAI = useAITransition();

  useEffect(() => {
    api.get("/relationships").then((r) => setRelationships(r.data));
    api.get("/settings/public")
      .then((r) => {
        setDeliveryPricing({ ...DEFAULT_DELIVERY_PRICING, ...(r.data.delivery_pricing || {}) });
        setBusinessWhatsapp(r.data.business_whatsapp || "");
      })
      .catch(() => {});
    const rid = params.get("relationship_id");
    const occ = params.get("occasion");
    const pin = params.get("pincode");
    if (rid) setRelationshipId(rid);
    if (occ) setOccasion(occ);
    if (pin && /^\d{6}$/.test(pin)) setPincode(pin);
  }, [params]);

  // Hydrate product cart items with latest delivery_pincode / is_global. Experiences skipped.
  useEffect(() => {
    const stale = items.filter(
      (i) => !isExperience(i) && i.product_id && (i.delivery_pincode == null || i.is_global == null)
    );
    if (stale.length === 0) return;
    Promise.all(stale.map((i) => api.get(`/products/${i.product_id}`).then((r) => r.data).catch(() => null)))
      .then((prods) => {
        const map = {};
        prods.filter(Boolean).forEach((p) => { map[p.id] = p; });
        const next = items.map((i) => {
          if (isExperience(i)) return i;
          const p = map[i.product_id];
          if (!p) return i;
          return {
            ...i,
            is_global: !!p.is_global,
            delivery_pincode: p.delivery_pincode != null ? String(p.delivery_pincode).trim() : null,
          };
        });
        setItems(next); setCart(next);
      });
  }, []);

  // Fetch minimum lead time for every product in the cart. Kept in a lookup
  // (not written back to the cart) so this feature doesn't change the cart
  // contract. Keyed by product_id. Also captures availability fields.
  const [productMeta, setProductMeta] = useState({}); // { [product_id]: {days, name, is_global, available_pincodes} }
  useEffect(() => {
    const ids = Array.from(new Set(
      items.filter((i) => !isExperience(i) && i.product_id).map((i) => i.product_id)
    )).filter((pid) => productMeta[pid] === undefined);
    if (ids.length === 0) return;
    Promise.all(ids.map((pid) => api.get(`/products/${pid}`).then((r) => r.data).catch(() => null)))
      .then((prods) => {
        setProductMeta((prev) => {
          const next = { ...prev };
          prods.filter(Boolean).forEach((p) => {
            next[p.id] = {
              days: p.min_lead_time_days || 0,
              name: p.name,
              image_url: p.image_url || (p.images && p.images[0]) || "",
              is_global: !!p.is_global,
              available_pincodes: (p.available_pincodes || []).map((x) => String(x).trim()),
              delivery_pincode: p.delivery_pincode || null,
              delivery_fee_type: p.delivery_fee_type || "free",
              delivery_fee_amount: p.delivery_fee_amount || 0,
            };
          });
          return next;
        });
      });
  }, [items]);

  // Backwards-compat: keep leadTimes for existing code paths.
  const leadTimes = productMeta;

  const updateQty = (lk, delta) => {
    const next = items.map((i) =>
      lineKeyOf(i) === lk && !isExperience(i)
        ? { ...i, quantity: Math.max(1, i.quantity + delta) }
        : i
    );
    setItems(next); setCart(next);
  };
  const remove = (lk) => {
    const next = items.filter((i) => lineKeyOf(i) !== lk);
    setItems(next); setCart(next);
    setExpDetails((d) => { const c = { ...d }; delete c[lk]; return c; });
  };

  const updateExp = (lk, patch) =>
    setExpDetails((d) => ({ ...d, [lk]: { ...(d[lk] || {}), ...patch } }));

  const openAddMore = () => {
    setPickerMode("add");
    setChangingLineKey(null);
    setChangingCategory(null);
    setPickerOpen(true);
  };
  const openChange = (item) => {
    setPickerMode("change");
    setChangingLineKey(lineKeyOf(item));
    setChangingCategory(item.category);
    setPickerOpen(true);
  };
  const closePicker = () => setPickerOpen(false);

  const handlePick = (p) => {
    const pImage = p.image_url || (p.images && p.images[0]) || "";
    if (pickerMode === "change" && changingLineKey) {
      // Replace the existing line, preserving its quantity.
      const next = items.map((i) => {
        if (lineKeyOf(i) !== changingLineKey) return i;
        return {
          line_key: p.id,
          kind: "product",
          product_id: p.id,
          name: p.name,
          category: p.category,
          price: p.price,
          quantity: i.quantity,
          image_url: pImage,
          is_global: !!p.is_global,
          delivery_pincode: p.delivery_pincode != null ? String(p.delivery_pincode).trim() : null,
        };
      });
      setItems(next); setCart(next);
      toast.success(`Replaced with ${p.name}`);
    } else {
      // Add mode: merge if already in cart, else append.
      const lk = p.id;
      const idx = items.findIndex((i) => lineKeyOf(i) === lk);
      let next;
      if (idx >= 0) {
        next = items.map((i, j) =>
          j === idx ? { ...i, quantity: i.quantity + 1 } : i
        );
      } else {
        next = [
          ...items,
          {
            line_key: lk,
            kind: "product",
            product_id: p.id,
            name: p.name,
            category: p.category,
            price: p.price,
            quantity: 1,
            image_url: pImage,
            is_global: !!p.is_global,
            delivery_pincode: p.delivery_pincode != null ? String(p.delivery_pincode).trim() : null,
          },
        ];
      }
      setItems(next); setCart(next);
      toast.success(`${p.name} added to cart`);
    }
    setPickerOpen(false);
  };

  const experienceItems = useMemo(() => items.filter(isExperience), [items]);

  const total = cartTotal(items);
  const cleanPin = String(pincode || "").trim();
  const hasOnRequest = experienceItems.some((i) => i.price_type === "on_request")
    || items.some(isSourceable);

  // ---- Lead-time validation ----
  // Compute the highest min_lead_time_days across products in the cart and
  // the earliest valid delivery date. If the selected date is too soon,
  // block submission and surface a targeted warning.
  const todayISO = new Date().toISOString().slice(0, 10);
  const addDays = (iso, n) => {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const humanDate = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      });
    } catch { return iso; }
  };
  const productLeadTimeItems = items
    .filter((i) => !isExperience(i) && i.product_id)
    .map((i) => ({
      line_key: lineKeyOf(i),
      name: i.name,
      days: leadTimes[i.product_id]?.days ?? 0,
    }));
  const maxLeadDays = productLeadTimeItems.reduce((m, x) => Math.max(m, x.days), 0);
  const earliestValidDate = maxLeadDays > 0 ? addDays(todayISO, maxLeadDays) : todayISO;
  // Delivery window rules: no same-day bookings, nothing beyond 90 days out,
  // and product lead-times can push the minimum further out.
  const tomorrowISO = addDays(todayISO, 1);
  const maxDeliveryDate = addDays(todayISO, 90);
  const minDeliveryDate = earliestValidDate > tomorrowISO ? earliestValidDate : tomorrowISO;
  const dateTooSoon = date < minDeliveryDate;
  const dateTooLate = date > maxDeliveryDate;
  const blockingLeadItems = dateTooSoon
    ? productLeadTimeItems.filter((x) => x.days === maxLeadDays && maxLeadDays > 0)
    : [];
  const blockingKeys = new Set(blockingLeadItems.map((x) => x.line_key));

  // ---- Dynamic delivery pricing (admin-configurable via /settings/public) ----
  // Express: same/next-day AND evening/night slot.
  // Standard: 2 days out OR morning/afternoon slot.
  // Plan Ahead: Standard booked N+ days ahead gets a discount (delivery only).
  // No product items (experiences/sourceable only) → nothing to deliver → ₹0.
  const hasProductItems = items.some((i) => !isExperience(i) && !isSourceable(i) && i.product_id);
  const daysAhead = Math.round(
    (new Date(date + "T00:00:00") - new Date(todayISO + "T00:00:00")) / 86400000
  );
  const isExpress = hasProductItems && date <= tomorrowISO && EXPRESS_SLOTS.has(slot);
  const isPlanAhead = hasProductItems && !isExpress && daysAhead >= Number(deliveryPricing.plan_ahead_min_days);
  const deliveryType = !hasProductItems ? null : isExpress ? "express" : "standard";
  const planAheadDiscount = isPlanAhead ? Number(deliveryPricing.plan_ahead_discount) : 0;
  const standardFee = Number(deliveryPricing.standard_fee);
  const deliveryFee = !hasProductItems ? 0
    : isExpress ? Number(deliveryPricing.express_fee)
    : standardFee - planAheadDiscount;
  const deliveryTypeLabel = deliveryType === "express"
    ? "Express Delivery · Arrives within your selected slot"
    : deliveryType === "standard"
      ? "Standard Delivery · Arrives within your selected slot"
      : "Delivery";
  const grandTotal = total + deliveryFee;

  // ---- Availability validation vs entered pincode ----
  const pinAvailFor = (meta, pin) => {
    if (!meta) return true; // unknown yet — do not falsely block
    if (meta.is_global) return true;
    const cp = String(pin || "").trim();
    if (!cp) return false;
    if ((meta.available_pincodes || []).length) return meta.available_pincodes.includes(cp);
    const legacy = String(meta.delivery_pincode || "").trim();
    return !!legacy && legacy === cp;
  };
  const unavailableItems = /^\d{6}$/.test(cleanPin)
    ? items.filter((i) => !isExperience(i) && i.product_id && !pinAvailFor(productMeta[i.product_id], cleanPin))
    : [];
  const hasUnavailable = unavailableItems.length > 0;
  const unavailableKeys = new Set(unavailableItems.map(lineKeyOf));
  // Experiences / on-request items are confirmed on WhatsApp first, then paid on the website.
  const needsWhatsapp = items.some((i) => isExperience(i) || isSourceable(i) || i.price_type === "on_request");

  const place = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!/^\d{6}$/.test(cleanPin)) { toast.error("Enter a valid 6-digit pincode"); return; }
    if (hasUnavailable) {
      toast.error(
        `${unavailableItems.length} item${unavailableItems.length > 1 ? "s" : ""} not available for your location. Please remove them or change your pincode.`
      );
      return;
    }
    if (dateTooSoon) {
      toast.error(
        blockingLeadItems.length > 0
          ? `Some items need at least ${maxLeadDays} day${maxLeadDays > 1 ? "s" : ""} preparation. Please select ${humanDate(minDeliveryDate)} or later.`
          : `Same-day delivery isn't available. Please select ${humanDate(minDeliveryDate)} or later.`
      );
      return;
    }
    if (dateTooLate) {
      toast.error(`We take bookings up to 90 days ahead. Please pick a date on or before ${humanDate(maxDeliveryDate)}.`);
      return;
    }
    // Validate required experience details.
    for (const ex of experienceItems) {
      const lk = lineKeyOf(ex);
      const d = expDetails[lk] || {};
      if (!d.date || !d.persons || !d.whatsapp) {
        toast.error(`Please fill date, persons and WhatsApp for "${ex.name}"`);
        return;
      }
      if (!/^\+?\d{10,15}$/.test(String(d.whatsapp).replace(/\s|-/g, ""))) {
        toast.error(`Enter a valid WhatsApp number for "${ex.name}"`);
        return;
      }
    }
    setBusy(true);
    // Open the chat tab synchronously so mobile browsers don't block it as a popup.
    const waWindow = needsWhatsapp && businessWhatsapp ? window.open("", "_blank") : null;
    try {
      const { data: booking } = await api.post("/bookings", {
        items: items.map((i) => {
          const lk = lineKeyOf(i);
          const base = {
            product_id: i.product_id || null,
            name: i.name,
            category: i.category,
            price: i.price,
            quantity: i.quantity,
            is_global: !!i.is_global,
            delivery_pincode: i.delivery_pincode != null ? String(i.delivery_pincode).trim() : null,
            kind: i.kind || "product",
            selected_weight: i.selected_weight || null,
            selected_flavor: i.selected_flavor || null,
            selected_variant: i.selected_variant || null,
          };
          if (isExperience(i)) {
            const d = expDetails[lk] || {};
            base.experience_id = i.experience_id;
            base.price_type = i.price_type;
            base.experience_details = {
              date: d.date || null,
              persons: d.persons ? Number(d.persons) : null,
              whatsapp: d.whatsapp || null,
              special_request: d.special_request || null,
            };
          }
          if (isSourceable(i)) {
            base.price_type = i.price_type || "on_request";
            base.source_description = i.source_description || null;
          }
          return base;
        }),
        date, time_slot: `${TIME_SLOTS.find((s) => s.key === slot)?.label || slot} (${TIME_SLOTS.find((s) => s.key === slot)?.range || ""})`,
        selected_date: date,
        selected_time_slot: slot,
        delivery_type: deliveryType,
        delivery_fee: deliveryFee,
        plan_ahead_discount: planAheadDiscount,
        address, phone, pincode: cleanPin,
        relationship_id: relationshipId || null,
        occasion: occasion || null,
        notes: notes || null,
        budget: (() => {
          try { return JSON.parse(localStorage.getItem("celebrai_cart_budget") || "null"); }
          catch { return null; }
        })(),
      });
      clearCart();
      try { localStorage.removeItem("celebrai_cart_budget"); } catch { /* noop */ }
      if (booking.confirmation_flow === "whatsapp") {
        if (waWindow) waWindow.location.href = waLink(businessWhatsapp, bookingChatText(booking.id));
        toast.success("Request saved! Chat with us on WhatsApp to confirm details — you'll pay here on CelebrAI once the final price is agreed.");
        navigate(`/bookings`);
        return;
      }
      if (booking.total > 0) {
        let result;
        try { result = await payForBooking(booking.id); } catch (e) { result = "error"; toast.error(e?.response?.data?.detail || e.message || "Could not start payment"); }
        if (result === "paid") toast.success("Payment received. Your booking is confirmed!");
        else if (result !== "error") toast.error("Payment not completed. Your booking is saved — tap Pay now on your bookings page to finish.");
        navigate(`/bookings`);
        return;
      }
      toast.success(
        hasOnRequest
          ? "Booking placed. Our team will call you to confirm price on request items."
          : "Booking placed. Our team will call you shortly."
      );
      navigate(`/bookings`);
    } catch (err) {
      if (waWindow) waWindow.close();
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-24" data-testid="checkout-empty">
        <ShoppingBag className="w-10 h-10 text-terracotta mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-3xl text-charcoal mb-2">Your cart is quiet</h1>
        <p className="text-charcoal/60 mb-6">Add flowers, cakes or a package to get started.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => navigate("/shop")} className="pill-btn pill-btn-primary" data-testid="go-shop">Go to shop</button>
          <button onClick={(e) => playAI(() => navigate("/planner"), e)}
            data-testid="checkout-ai-cta"
            className="inline-flex items-center gap-2 rounded-full border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white px-4 py-2 text-sm font-bold transition-colors">
            <Sparkles className="w-4 h-4" strokeWidth={2.25} /> Complete My Celebration
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="checkout-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl text-charcoal">Checkout</h1>
          <p className="text-charcoal/60 mt-2">A concierge will confirm your booking within the hour.</p>
        </div>
        <button
          type="button"
          onClick={(e) => playAI(() => navigate("/planner"), e)}
          data-testid="checkout-ai-cta"
          className="inline-flex items-center gap-2 rounded-full border-2 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white px-4 py-2 text-sm font-bold transition-colors"
        >
          <Sparkles className="w-4 h-4" strokeWidth={2.25} /> ✨ Complete My Celebration
        </button>
      </div>

      <form onSubmit={place} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-surface p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">Items</p>
              <button
                type="button"
                onClick={openAddMore}
                className="inline-flex items-center gap-1.5 rounded-full border border-terracotta/40 text-terracotta px-3 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider hover:bg-terracotta hover:text-white transition-all whitespace-nowrap"
                data-testid="add-more-products"
              >
                <PlusCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
                Add more
              </button>
            </div>
            {hasUnavailable && (
              <div className="mb-4 rounded-2xl border border-rose-300 bg-rose-50 p-4 flex gap-3"
                data-testid="unavailable-warning">
                <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-rose-800">
                    {unavailableItems.length} item{unavailableItems.length > 1 ? "s" : ""} in your cart {unavailableItems.length > 1 ? "are" : "is"} not available for pincode {cleanPin}.
                  </p>
                  <p className="text-xs text-rose-700 mt-1">
                    Please remove or replace: <span className="font-semibold" data-testid="unavailable-names">
                      {unavailableItems.map((x) => x.name).join(", ")}
                    </span>
                  </p>
                </div>
              </div>
            )}
            <div className="divide-y divide-warm">
              {items.map((i, idx) => {
                const lk = lineKeyOf(i);
                const exp = isExperience(i);
                const src = isSourceable(i);
                const pid = i.product_id;
                const itemLeadDays = pid ? (leadTimes[pid]?.days || 0) : 0;
                const isBlocking = !exp && !src && blockingKeys.has(lk);
                const isUnavailable = !exp && !src && unavailableKeys.has(lk);
                return (
                  <div
                    key={lk}
                    className={`py-4 ${
                      isUnavailable ? "-mx-3 px-3 rounded-2xl bg-rose-50/80 ring-1 ring-rose-300" :
                      isBlocking ? "-mx-3 px-3 rounded-2xl bg-rose-50/60 ring-1 ring-rose-200" : ""
                    }`}
                    data-testid={`cart-item-${idx}`}
                  >
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      <div className="flex items-start gap-3 sm:gap-4 basis-full sm:basis-0 sm:flex-1 min-w-0">
                        <CartItemThumb
                          src={i.image_url || (pid && productMeta[pid]?.image_url) || ""}
                          alt={i.name}
                          kind={i.kind || "product"}
                          testId={`item-thumb-${idx}`}
                        />
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {exp && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                              data-testid={`item-experience-badge-${idx}`}>
                              <PartyPopper className="w-3 h-3" strokeWidth={2.5} /> Experience
                            </span>
                          )}
                          {src && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 border border-violet-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                              data-testid={`item-sourceable-badge-${idx}`}>
                              <PartyPopper className="w-3 h-3" strokeWidth={2.5} /> Sourceable · Can be arranged
                            </span>
                          )}
                          {!exp && isUnavailable && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                              data-testid={`item-unavailable-badge-${idx}`}
                            >
                              <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                              Not available for your location
                            </span>
                          )}
                          {!exp && itemLeadDays > 0 && (
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${
                                isBlocking
                                  ? "bg-rose-100 text-rose-700 border-rose-200"
                                  : "bg-sky-50 text-sky-700 border-sky-200"
                              }`}
                              data-testid={`lead-time-badge-${idx}`}
                            >
                              <Clock className="w-3 h-3" strokeWidth={2.5} />
                              {itemLeadDays} day{itemLeadDays > 1 ? "s" : ""} lead time
                            </span>
                          )}
                        </div>
                        <p className="font-display text-charcoal mt-0.5 break-words">{i.name}</p>
                        <p className="text-xs text-charcoal/60 capitalize">{i.category}</p>
                        {src && i.source_description && (
                          <p className="text-xs text-charcoal/60 mt-1 line-clamp-2">{i.source_description}</p>
                        )}
                        {!exp && !src && (
                          <button
                            type="button"
                            onClick={() => openChange(i)}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-terracotta hover:text-terracotta/80 uppercase tracking-widest"
                            data-testid={`change-product-${idx}`}
                          >
                            <Repeat className="w-3 h-3" strokeWidth={2.5} />
                            Change
                          </button>
                        )}
                        </div>
                      </div>
                      {!exp && !src && (
                        <div className="flex items-center gap-2 border border-warm rounded-full flex-shrink-0">
                          <button type="button" onClick={() => updateQty(lk, -1)} className="p-2" data-testid={`qty-dec-${idx}`}><Minus className="w-3 h-3" /></button>
                          <span className="text-sm font-semibold w-6 text-center">{i.quantity}</span>
                          <button type="button" onClick={() => updateQty(lk, +1)} className="p-2" data-testid={`qty-inc-${idx}`}><Plus className="w-3 h-3" /></button>
                        </div>
                      )}
                      <p className="font-semibold text-charcoal min-w-0 flex-1 sm:flex-none sm:w-32 text-right whitespace-nowrap truncate" data-testid={`item-price-${idx}`}>
                        {priceLabel(i)}
                      </p>
                      <button type="button" onClick={() => remove(lk)} className="p-2 text-charcoal/60 hover:text-terracotta flex-shrink-0" data-testid={`remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {exp && (
                      <ExperienceFields
                        idx={idx}
                        detail={expDetails[lk] || {}}
                        onChange={(patch) => updateExp(lk, patch)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-surface p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-4">Delivery details</p>
            {dateTooSoon && blockingLeadItems.length > 0 && (
              <div
                className="mb-4 rounded-2xl border border-rose-300 bg-rose-50 p-4 flex gap-3"
                data-testid="lead-time-warning"
              >
                <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-rose-800">
                    Some items in your cart need at least {maxLeadDays} day{maxLeadDays > 1 ? "s" : ""} preparation time.
                    Please select a delivery date of{" "}
                    <span className="font-bold" data-testid="lead-time-earliest-date">{humanDate(minDeliveryDate)}</span>{" "}
                    or later.
                  </p>
                  <p className="text-xs text-rose-700 mt-1">
                    Items requiring the longer lead time:{" "}
                    <span data-testid="lead-time-blocking-items">
                      {blockingLeadItems.map((x) => x.name).join(", ")}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setDate(minDeliveryDate)}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5"
                    data-testid="lead-time-fix-date"
                  >
                    Use {humanDate(minDeliveryDate)}
                  </button>
                </div>
              </div>
            )}
            {dateTooSoon && blockingLeadItems.length === 0 && (
              <p className="mb-4 text-xs font-semibold text-rose-700" data-testid="min-date-hint">
                Same-day delivery isn&apos;t available — please pick {humanDate(minDeliveryDate)} or later.
              </p>
            )}
            {dateTooLate && (
              <p className="mb-4 text-xs font-semibold text-rose-700" data-testid="max-date-hint">
                We take bookings up to 90 days ahead — please pick a date on or before {humanDate(maxDeliveryDate)}.
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Delivery date">
                <input type="date" required value={date} min={minDeliveryDate} max={maxDeliveryDate} onChange={(e) => setDate(e.target.value)}
                  className={`w-full rounded-xl bg-white border px-4 py-3 focus:outline-none focus:border-terracotta ${
                    dateTooSoon || dateTooLate ? "border-rose-400 ring-2 ring-rose-100" : "border-warm"
                  }`}
                  data-testid="checkout-date" />
                <p className="text-[11px] text-charcoal/50 mt-1">Earliest {humanDate(minDeliveryDate)} · up to 90 days ahead</p>
              </Field>
              <Field label="Time slot">
                <select value={slot} onChange={(e) => setSlot(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta" data-testid="checkout-slot">
                  {TIME_SLOTS.map((s) => (
                    <option key={s.key} value={s.key} data-testid={`slot-option-${s.key}`}>
                      {s.label} · {s.range}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Delivery address (Jaipur)">
                  <textarea required rows={2} value={address} onChange={(e) => setAddress(e.target.value)}
                    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta resize-none" data-testid="checkout-address" />
                </Field>
              </div>
              <Field label="Contact phone">
                <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta" data-testid="checkout-phone" />
              </Field>
              <Field label="Pincode">
                <input required type="text" inputMode="numeric" maxLength={6} pattern="\d{6}" value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit pincode"
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta"
                  data-testid="checkout-pincode" />
              </Field>
              <Field label="Notes (optional)">
                <input value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta" data-testid="checkout-notes" />
              </Field>
              <Field label="Celebrating for">
                <select value={relationshipId} onChange={(e) => setRelationshipId(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta" data-testid="checkout-relationship">
                  <option value="">— none —</option>
                  {relationships.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.relationship_type})</option>)}
                </select>
              </Field>
              <Field label="Occasion">
                <select value={occasion} onChange={(e) => setOccasion(e.target.value)}
                  className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-terracotta" data-testid="checkout-occasion">
                  <option value="">—</option>
                  <option>Birthday</option>
                  <option>Anniversary</option>
                  <option>Other</option>
                </select>
              </Field>
            </div>
          </div>
        </div>

        <aside className="card-surface p-6 h-fit sticky top-24">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-4">Summary</p>
          <div className="space-y-2 text-sm text-charcoal/80">
            <Row label="Subtotal" value={`₹${total}`} />
            <div className="flex items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block" data-testid="delivery-type-label">{deliveryTypeLabel}</span>
                {deliveryType && (
                  <span className="block text-[11px] text-charcoal/50">
                    {TIME_SLOTS.find((s) => s.key === slot)?.label} · {TIME_SLOTS.find((s) => s.key === slot)?.range} · {humanDate(date)}
                  </span>
                )}
                {planAheadDiscount > 0 && (
                  <span
                    className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
                    data-testid="plan-ahead-badge"
                  >
                    🎉 Plan Ahead &amp; Save ₹{planAheadDiscount}
                  </span>
                )}
              </span>
              <span className="font-semibold whitespace-nowrap text-right" data-testid="checkout-delivery-fee">
                {deliveryFee > 0 ? (
                  planAheadDiscount > 0 ? (
                    <>
                      <span className="line-through text-charcoal/40 font-normal mr-1.5" data-testid="delivery-fee-original">₹{standardFee}</span>
                      <span data-testid="delivery-fee-final">₹{deliveryFee}</span>
                    </>
                  ) : `₹${deliveryFee}`
                ) : "Free"}
              </span>
            </div>
            <Row label="Taxes" value="Included" />
          </div>
          {hasOnRequest && (
            <p className="text-[11px] text-violet-600 mt-2" data-testid="on-request-hint">
              Some items are priced on request — the concierge will confirm cost by WhatsApp before your event.
            </p>
          )}
          {!cleanPin && (
            <p className="text-[11px] text-charcoal/50 mt-2" data-testid="pincode-hint">Enter your pincode to confirm delivery charges.</p>
          )}
          <div className="mt-4 pt-4 border-t border-warm flex items-center justify-between">
            <span className="font-display text-lg text-charcoal">Total</span>
            <span className="font-display text-2xl text-charcoal" data-testid="checkout-total">₹{grandTotal}</span>
          </div>
          <button type="submit" disabled={busy || dateTooSoon || dateTooLate || hasUnavailable} className="w-full pill-btn pill-btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="place-order">
            {busy ? "Processing..." : needsWhatsapp ? "Confirm via WhatsApp" : "Confirm & Pay"}
          </button>
          <p className="text-xs text-charcoal/60 mt-3 text-center" data-testid="checkout-payment-note">
            {needsWhatsapp
              ? "We'll confirm availability and the final price with you on WhatsApp. You'll then pay securely here on CelebrAI."
              : "Pay securely online by card, UPI or netbanking."}
          </p>
        </aside>
      </form>

      <ProductPickerModal
        open={pickerOpen}
        mode={pickerMode}
        pincode={cleanPin}
        restrictCategory={pickerMode === "change" ? changingCategory : undefined}
        excludeIds={
          pickerMode === "change"
            ? items.filter((i) => lineKeyOf(i) === changingLineKey && i.product_id).map((i) => i.product_id)
            : []
        }
        onPick={handlePick}
        onClose={closePicker}
      />
    </div>
  );
}

function ExperienceFields({ idx, detail, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div
      className="mt-3 ml-1 rounded-2xl border border-rose-200 bg-rose-50/50 p-4"
      data-testid={`experience-fields-${idx}`}
    >
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-700 mb-3">
        Experience details
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Preferred date">
          <input
            required
            type="date"
            min={today}
            value={detail.date || ""}
            onChange={(e) => onChange({ date: e.target.value })}
            className="w-full rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`exp-date-${idx}`}
          />
        </Field>
        <Field label="Number of persons">
          <input
            required
            type="number"
            min={1}
            max={100}
            value={detail.persons || ""}
            onChange={(e) => onChange({ persons: e.target.value.replace(/\D/g, "") })}
            placeholder="e.g. 2"
            className="w-full rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`exp-persons-${idx}`}
          />
        </Field>
        <Field label="WhatsApp number">
          <input
            required
            type="tel"
            inputMode="tel"
            value={detail.whatsapp || ""}
            onChange={(e) => onChange({ whatsapp: e.target.value })}
            placeholder="e.g. +91 98765 43210"
            className="w-full rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`exp-whatsapp-${idx}`}
          />
        </Field>
        <Field label="Special request (optional)">
          <input
            type="text"
            value={detail.special_request || ""}
            onChange={(e) => onChange({ special_request: e.target.value })}
            placeholder="e.g. vegan menu, roses only, surprise moment"
            className="w-full rounded-xl bg-white border border-warm px-3 py-2.5 focus:outline-none focus:border-rose-400 text-sm"
            data-testid={`exp-note-${idx}`}
          />
        </Field>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">{label}</label>
    {children}
  </div>
);
const Row = ({ label, value, testId }) => (
  <div className="flex justify-between" data-testid={testId}><span>{label}</span><span>{value}</span></div>
);

/**
 * Cart line thumbnail. Renders the actual inventory image the customer
 * added; falls back to a clean icon placeholder if the URL is missing or
 * the image fails to load (avoids the broken-image glyph).
 */
function CartItemThumb({ src, alt, kind, testId }) {
  const [failed, setFailed] = useState(false);
  const showImage = !!src && !failed;
  const FallbackIcon = kind === "experience" ? PartyPopper : Package;
  return (
    <div
      className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden bg-linen-2 flex items-center justify-center flex-shrink-0"
      data-testid={testId}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt || ""}
          loading="lazy"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
          data-testid={`${testId}-img`}
        />
      ) : (
        <FallbackIcon
          className="w-6 h-6 text-charcoal/40"
          strokeWidth={1.5}
          data-testid={`${testId}-fallback`}
        />
      )}
    </div>
  );
}
